import React, { useState, useEffect, useRef, useCallback } from "react";
import { Bell, CheckCheck, MapPin } from "lucide-react";
import { toast } from "react-toastify";
import {
  fetchStayAlertsAPI,
  markStayAlertsReadAPI,
} from "../actioncreators/actioncreators";
import { usePermission } from "../contexts/usePermission";

// Held only by orgs with BLE attendance enabled (see the org's
// beacon_tracking_enabled toggle), so the bell disappears for other orgs.
const STAY_ALERT_PERMISSION = "FEATURE_STUDENT_STAY_ALERT_VIEW";

// How often the bell re-checks for new alerts. Alerts are raised by a cron job
// that runs every 2 minutes, so polling faster than this gains nothing.
const POLL_INTERVAL_MS = 60 * 1000;

const formatDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

// Notification bell for the header: lists the logged-in manager/admin's
// student stay-break alerts (a student left a beacon before its required
// stay time). Read/unread state is per person, tracked server-side.
const NotificationBell = () => {
  const { hasPermission, loading: permissionsLoading } = usePermission();
  const allowed = !permissionsLoading && hasPermission(STAY_ALERT_PERMISSION);
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef(null);
  // Highest unread count already seen, so a toast only fires for alerts that
  // arrived after the first load rather than on every page open.
  const lastUnreadRef = useRef(null);

  const loadAlerts = useCallback(async () => {
    try {
      const response = await fetchStayAlertsAPI(50);
      if (response?.businessStatusCode === 2) {
        const nextUnread = response.unreadCount || 0;
        setAlerts(response.alerts || []);
        setUnreadCount(nextUnread);
        if (lastUnreadRef.current !== null && nextUnread > lastUnreadRef.current) {
          const newest = (response.alerts || []).find((a) => !a.isRead);
          toast.warn(
            newest
              ? `${newest.studentName || "A student"} left ${newest.beaconName || "the beacon"} early`
              : "New student alert"
          );
        }
        lastUnreadRef.current = nextUnread;
      }
    } catch (error) {
      // A failed poll is not worth interrupting the user for; the next tick retries.
      console.error("Failed to load stay alerts:", error);
    }
  }, []);

  useEffect(() => {
    if (!allowed) return undefined;
    loadAlerts();
    const timer = setInterval(loadAlerts, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [allowed, loadAlerts]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = async () => {
    const opening = !isOpen;
    setIsOpen(opening);
    if (opening) {
      setIsLoading(true);
      await loadAlerts();
      setIsLoading(false);
    }
  };

  const markRead = async (requestBody, localUpdate) => {
    try {
      const response = await markStayAlertsReadAPI(requestBody);
      if (response?.businessStatusCode === 2) {
        setAlerts((prev) => prev.map(localUpdate));
        // Recompute from the server's view on the next poll; update locally now.
        setUnreadCount((prev) => {
          const next = requestBody.all ? 0 : Math.max(0, prev - requestBody.alertIds.length);
          lastUnreadRef.current = next;
          return next;
        });
      }
    } catch (error) {
      toast.error("Could not update notifications.");
    }
  };

  const handleItemClick = (alert) => {
    if (alert.isRead) return;
    markRead({ alertIds: [alert.alertId] }, (a) =>
      a.alertId === alert.alertId ? { ...a, isRead: true } : a
    );
  };

  const handleMarkAllRead = () => {
    markRead({ all: true }, (a) => ({ ...a, isRead: true }));
  };

  if (!allowed) return null;

  return (
    <div className="relative ms-2" ref={containerRef}>
      <style>
        {`
          .notif-bell-btn { position: relative; }
          .notif-badge {
            position: absolute;
            top: -4px;
            right: -4px;
            min-width: 16px;
            height: 16px;
            padding: 0 4px;
            border-radius: 8px;
            background: #dc2626;
            color: #fff;
            font-size: 10px;
            font-weight: 700;
            line-height: 16px;
            text-align: center;
          }
          .notif-dropdown {
            position: absolute;
            top: 100%;
            right: 0;
            margin-top: 6px;
            width: 340px;
            max-width: calc(100vw - 24px);
            background: #fff;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
            /* Above the mobile sidebar drawer (z-index: 1050), same as the
               other header dropdowns. */
            z-index: 1100;
            max-height: 420px;
            overflow-y: auto;
          }
          .notif-header {
            position: sticky;
            top: 0;
            z-index: 1;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px 14px;
            background: #fff;
            border-bottom: 1px solid #e5e7eb;
            color: #111827;
            font-size: 13px;
            font-weight: 600;
          }
          .notif-mark-all {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            border: none;
            background: transparent;
            color: #fd610a;
            font-size: 12px;
            font-weight: 600;
            cursor: pointer;
          }
          .notif-mark-all:disabled { color: #9ca3af; cursor: default; }
          .notif-item {
            padding: 10px 14px;
            border-bottom: 1px solid #f3f4f6;
            cursor: pointer;
            color: #374151;
          }
          .notif-item:last-child { border-bottom: none; }
          .notif-item:hover { background: #f9fafb; }
          .notif-item.unread { background: #fff7ed; }
          .notif-item.unread:hover { background: #ffedd5; }
          .notif-title { font-size: 13px; font-weight: 600; color: #111827; }
          .notif-meta { font-size: 11px; color: #6b7280; margin-top: 2px; }
          .notif-empty { padding: 24px 14px; text-align: center; font-size: 13px; color: #6b7280; }
        `}
      </style>

      <button
        type="button"
        className="notif-bell-btn p-2 bg-gray-200 rounded-full d-flex align-items-center justify-content-center"
        style={{ width: "32px", height: "32px" }}
        onClick={handleToggle}
        title="Notifications"
        aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ""}`}
      >
        <Bell className="text-gray-600" size={16} />
        {unreadCount > 0 && (
          <span className="notif-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notif-dropdown">
          <div className="notif-header">
            <span>Notifications</span>
            <button
              type="button"
              className="notif-mark-all"
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0}
            >
              <CheckCheck size={14} />
              Mark all read
            </button>
          </div>

          {isLoading && alerts.length === 0 ? (
            <div className="notif-empty">Loading...</div>
          ) : alerts.length === 0 ? (
            <div className="notif-empty">No notifications</div>
          ) : (
            alerts.map((alert) => (
              <div
                key={alert.alertId}
                className={`notif-item ${alert.isRead ? "" : "unread"}`}
                onClick={() => handleItemClick(alert)}
              >
                <div className="notif-title">
                  {alert.studentName || "A student"} left early
                </div>
                <div className="notif-meta" style={{ color: "#374151", fontSize: 12 }}>
                  Left {alert.beaconName || "the beacon"} after {alert.stayedMinutes} of{" "}
                  {alert.requiredStayMinutes} required minutes.
                </div>
                <div className="notif-meta">
                  {alert.locationName && (
                    <span style={{ marginRight: 8 }}>
                      <MapPin size={10} style={{ marginRight: 2 }} />
                      {alert.locationName}
                    </span>
                  )}
                  {formatDateTime(alert.leftAt)}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
